import type { QueryClient } from '@tanstack/react-query';
import { getGetTasksQueryKey, type KdsTaskDetail, type KdsTaskDetailStatus } from '@ordr-as/ordr-service-types/epos';

const INVALIDATE_DEBOUNCE_MS = 300;

// `kds.tasks.added` and `kds.task.updated` share this shape.
type TaskEventPayload = {
  stationId: string;
  tasks: {
    taskId: string;
    stationId: string;
    eventType: string;
    payload: {
      bookingOrderId: number;
      groupId: string;
      status: string;
    };
  }[];
};

/**
 * Interprets KDS websocket messages and syncs them into the react-query tasks
 * cache. This is the only place that knows both the wire payloads and the
 * cache layout; the websocket client stays a plain transport.
 */
export function createKdsTasksCacheSync(queryClient: QueryClient, locationId: number) {
  const tasksQueryKey = getGetTasksQueryKey(locationId);
  let invalidateTimer: number | undefined;

  const scheduleInvalidate = () => {
    // Debounced so a burst of events triggers a single request.
    clearTimeout(invalidateTimer);
    invalidateTimer = window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: tasksQueryKey });
    }, INVALIDATE_DEBOUNCE_MS);
  };

  const onSubscribed = () => {
    // GET /tasks is the source of truth for current state. Rather than seeding
    // the cache from the websocket snapshot, refetch from the API at the
    // (re)subscribe sync point and let the socket only drive updates after.
    scheduleInvalidate();
  };

  const onTaskEvent = (payload: TaskEventPayload) => {
    // The push payload has no items, so only patch statuses of tasks we already
    // know (never insert skeleton tasks) and refetch the full truth.
    const statusByTaskId = new Map(payload.tasks.map((t) => [Number(t.taskId), t.payload.status]));
    queryClient.setQueryData<KdsTaskDetail[]>(tasksQueryKey, (current) =>
      current?.map((task) => {
        const status = task.id === undefined ? undefined : statusByTaskId.get(task.id);
        return status ? { ...task, status: status as KdsTaskDetailStatus } : task;
      })
    );

    scheduleInvalidate();
  };

  return {
    onMessage: (type: string, data: unknown) => {
      if (type === 'subscribe.v1.kds.ok') onSubscribed();
      if (type === 'kds.tasks.added') onTaskEvent(data as TaskEventPayload);
      if (type === 'kds.task.updated') onTaskEvent(data as TaskEventPayload);
    },
    dispose: () => clearTimeout(invalidateTimer),
  };
}
